import { FC, useEffect, useState } from "react";
import CommonLayout from "./CommonLayout";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentHolidayPackage, setHolidayPackage } from "application/features/holidayPackage/holidayPackageSlice";
import Label from "components/Label/Label";
import { useTranslation } from "react-i18next";
import ChangingButton from "shared/Button/ChangingButton";
import { set } from "lodash";

export interface AddHolidayPackage4Props { }

const PageAddHolidayPackage4: FC<AddHolidayPackage4Props> = () => {

    const { t, ready } = useTranslation("translation");
    const holidayPackage = useSelector(selectCurrentHolidayPackage);
    const dispatch = useDispatch();
    const [selectedButton, setSelectedButton] = useState<boolean[]>([false, false, false, false]);

    const handleUpdateHolidayPackage = async (field: string, value: any) => {
        dispatch(setHolidayPackage({ ...holidayPackage, [field]: value }));
        const updatedSelectedButton = [...selectedButton];
        const index = ["defined_guests", "sharing_group", "organized_group", "organized_privacy"].indexOf(value);
        if (index !== -1) {
            let condition = updatedSelectedButton[index];
            updatedSelectedButton.fill(false); // Reset all buttons
            updatedSelectedButton[index] = !condition; // Set the clicked button to true
            setSelectedButton(updatedSelectedButton);
        } else {
            setSelectedButton([false, false, false, false]);
        }
    };

    const nextPageLink = () => {
        switch (holidayPackage?.package_type) {
            case "defined_guests":
                return "/host/new-holiday-package/add-listing-5a";
            case "sharing_group":
                return "/host/new-holiday-package/add-listing-5b";
            case "organized_group":
                return "/host/new-holiday-package/add-listing-5b"
            case "organized_privacy":
                return "/host/new-holiday-package/add-listing-5b";
            default:
                break;
        }
    }

    const _updateDefaultFields = async () => {
        dispatch(setHolidayPackage({ 
            ...holidayPackage, 
            package_type: holidayPackage?.package_type? holidayPackage.package_type : undefined
        }));
        if (holidayPackage?.package_type) {
            const index = ["defined_guests", "sharing_group", "organized_group", "organized_privacy"].indexOf(holidayPackage.package_type);
            if (index !== -1) {
                let updatedSelectedButton = [...selectedButton];
                updatedSelectedButton.fill(false); // Reset all buttons
                updatedSelectedButton[index] = true; // Set the clicked button to true
                setSelectedButton(updatedSelectedButton);
            }
        }
    }

    function nextPageCondition() {
        return selectedButton.some((item) => item === true);
    }

    useEffect(() => {
        _updateDefaultFields();
    }, []);

    if (!ready) return null;

    return (
        <CommonLayout
            index="04"
            backtHref={"/host/new-holiday-package/add-listing-3"}
            nextHref={nextPageLink()}
            canGoNext={nextPageCondition()}
        >
        <>
            <h2 className="text-2xl font-semibold">{t("translation:components.holidayPackageForm.page4.title")}</h2>
            <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
            {/* FORM */}
            <div className="space-y-8">
                {/* ITEM */}

                <div className="flex flex-col space-y-8">

                    <div className="flex flex-col md:flex-row space-x-2 items-center justify-between">
                        <div className="w-3/4 md:pr-8">
                            <h2 className="text-xl font-semibold">
                                {t("translation:components.holidayPackageForm.page4.form.predefinedPackage")}
                            </h2>
                            <p className="text-sm text-justify text-neutral-500 dark:text-neutral-200">
                                {t("translation:components.holidayPackageForm.page4.form.predefinedPackageDescription")}
                            </p>
                        </div>
                        <ChangingButton
                            className="mt-4 md:mt-0 w-1/4"
                            onClick={() => handleUpdateHolidayPackage("package_type", "defined_guests")}
                            selected={selectedButton[0]}
                        >
                            {t("translation:components.holidayPackageForm.page4.form.confirmation")}
                        </ChangingButton>
                    </div>

                    <div className="flex flex-col md:flex-row space-x-2 items-center justify-between">
                        <div className="w-3/4 md:pr-8">
                            <h2 className="text-xl font-semibold">
                                {t("translation:components.holidayPackageForm.page4.form.groupSharingPackage")}
                            </h2>
                            <p className="text-sm text-justify text-neutral-500 dark:text-neutral-200">
                                {t("translation:components.holidayPackageForm.page4.form.groupSharingPackageDescription")}
                            </p>
                        </div>
                        <ChangingButton
                            className="mt-4 md:mt-0 w-1/4"
                            onClick={() => handleUpdateHolidayPackage("package_type", "sharing_group")}
                            selected={selectedButton[1]}
                        >
                            {t("translation:components.holidayPackageForm.page4.form.confirmation")}
                        </ChangingButton>
                    </div>

                    <div className="flex flex-col md:flex-row space-x-2 items-center justify-between">
                        <div className="w-3/4 md:pr-8">
                            <h2 className="text-xl font-semibold">
                                {t("translation:components.holidayPackageForm.page4.form.organizedGroupPackage")}
                            </h2>
                            <p className="text-sm text-justify text-neutral-500 dark:text-neutral-200">
                                {t("translation:components.holidayPackageForm.page4.form.organizedGroupPackageDescription")}
                            </p>
                        </div>
                        <ChangingButton
                            className="mt-4 md:mt-0 w-1/4"
                            onClick={() => handleUpdateHolidayPackage("package_type", "organized_group")}
                            selected={selectedButton[2]}
                        >
                            {t("translation:components.holidayPackageForm.page4.form.confirmation")}
                        </ChangingButton>
                    </div>

                    <div className="flex flex-col md:flex-row space-x-2 items-center justify-between">
                        <div className="w-3/4 md:pr-8">
                            <h2 className="text-xl font-semibold">
                                {t("translation:components.holidayPackageForm.page4.form.organizedGroupPlusPackage")}
                            </h2>
                            <p className="text-sm text-justify text-neutral-500 dark:text-neutral-200">
                                {t("translation:components.holidayPackageForm.page4.form.organizedGroupPlusPackageDescription")}
                            </p>
                        </div>
                        <ChangingButton
                            className="mt-4 md:mt-0 w-1/4"
                            onClick={() => handleUpdateHolidayPackage("package_type", "organized_privacy")}
                            selected={selectedButton[3]}
                        >
                            {t("translation:components.holidayPackageForm.page4.form.confirmation")}
                        </ChangingButton>
                    </div>

                </div>
            
            </div>
        </>
        </CommonLayout >
    );
};

export default PageAddHolidayPackage4;